/**
 * Base maps and raster overlays of geo camera plans. Mapbox styles carry the
 * three standard base maps; regional maps are raster tiles offered only
 * inside their bounding box.
 */
/** Satellite / Map / Topo, as in the design's segmented control. */
export const STANDARD_BASE_MAPS = [
    {
        id: "sat",
        label: "Satellite",
        kind: "mapbox",
        style: "mapbox://styles/mapbox/satellite-v9",
        imagery: true,
        attribution: "Aerial image © Mapbox, © Maxar",
    },
    {
        id: "map",
        label: "Map",
        kind: "mapbox",
        style: "mapbox://styles/mapbox/outdoors-v12",
        hasContours: true,
    },
    {
        id: "topo",
        label: "Topo",
        kind: "mapbox",
        style: "mapbox://styles/yannisgu/clxf7ti86005x01pf24f7bb6m",
        hasContours: true,
    },
];
const SWITZERLAND = [5.95, 45.81, 10.5, 47.81];
const FRANCE = [-5.2, 41.3, 9.6, 51.15];
const GERMANY = [5.85, 47.25, 15.05, 55.1];
const AUSTRIA = [9.5, 46.35, 17.17, 49.03];
function wmtsGeoAdmin(layer, format) {
    return `https://wmts.geo.admin.ch/1.0.0/${layer}/default/current/3857/{z}/{x}/{y}.${format}`;
}
function wmtsIgn(layer, format) {
    return `https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=${layer}&STYLE=normal&TILEMATRIXSET=PM&FORMAT=${format}&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}`;
}
export const REGIONAL_BASE_MAPS = [
    {
        id: "ch-lk",
        label: "swisstopo map",
        kind: "raster",
        tiles: [wmtsGeoAdmin("ch.swisstopo.pixelkarte-farbe", "jpeg")],
        tileSize: 256,
        maxZoom: 18,
        bbox: SWITZERLAND,
        attribution: "© swisstopo",
        region: "Switzerland",
    },
    {
        id: "ch-img",
        label: "SWISSIMAGE",
        imagery: true,
        kind: "raster",
        tiles: [wmtsGeoAdmin("ch.swisstopo.swissimage", "jpeg")],
        tileSize: 256,
        maxZoom: 20,
        bbox: SWITZERLAND,
        attribution: "© swisstopo",
        region: "Switzerland",
    },
    {
        id: "fr-plan",
        label: "IGN Plan v2",
        kind: "raster",
        tiles: [wmtsIgn("GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2", "image/png")],
        tileSize: 256,
        maxZoom: 19,
        bbox: FRANCE,
        attribution: "© IGN",
        region: "France",
    },
    {
        id: "fr-ortho",
        label: "IGN Ortho",
        imagery: true,
        kind: "raster",
        tiles: [wmtsIgn("ORTHOIMAGERY.ORTHOPHOTOS", "image/jpeg")],
        tileSize: 256,
        maxZoom: 19,
        bbox: FRANCE,
        attribution: "© IGN",
        region: "France",
    },
    {
        id: "de-topplus",
        label: "BKG TopPlusOpen",
        kind: "raster",
        tiles: [
            "https://sgx.geodatenzentrum.de/wmts_topplus_open/tile/1.0.0/web/default/WEBMERCATOR/{z}/{y}/{x}.png",
        ],
        tileSize: 256,
        maxZoom: 18,
        bbox: GERMANY,
        attribution: "© BKG (2026), dl-de/by-2-0",
        region: "Germany",
    },
    {
        id: "at-map",
        label: "basemap.at",
        kind: "raster",
        tiles: [
            "https://mapsneu.wien.gv.at/basemap/geolandbasemap/normal/google3857/{z}/{y}/{x}.png",
        ],
        tileSize: 256,
        maxZoom: 19,
        bbox: AUSTRIA,
        attribution: "© basemap.at",
        region: "Austria",
    },
    {
        id: "at-ortho",
        label: "basemap.at Orthofoto",
        imagery: true,
        kind: "raster",
        tiles: [
            "https://mapsneu.wien.gv.at/basemap/bmaporthofoto30cm/normal/google3857/{z}/{y}/{x}.jpeg",
        ],
        tileSize: 256,
        maxZoom: 19,
        bbox: AUSTRIA,
        attribution: "© basemap.at",
        region: "Austria",
    },
];
export const BASE_MAPS = [
    ...STANDARD_BASE_MAPS,
    ...REGIONAL_BASE_MAPS,
];
export function getBaseMap(id) {
    return BASE_MAPS.find((b) => b.id === id) ?? STANDARD_BASE_MAPS[0];
}
export function bboxIntersects(a, b) {
    return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}
/** Regional base maps that cover (part of) an area. */
export function regionalBaseMapsFor(area) {
    if (!area)
        return [];
    return REGIONAL_BASE_MAPS.filter((b) => bboxIntersects(b.bbox, area));
}
/** Overlay toggles that are plain raster tiles. */
export const RASTER_OVERLAYS = [
    {
        id: "antennas",
        label: "Mobile antennas (CH)",
        tiles: [wmtsGeoAdmin("ch.bakom.standorte-mobilfunkanlagen", "png")],
        tileSize: 256,
        bbox: SWITZERLAND,
        attribution: "Antennas © BAKOM",
    },
];
export function rasterOverlaysFor(area) {
    if (!area)
        return [];
    return RASTER_OVERLAYS.filter((o) => bboxIntersects(o.bbox, area));
}
/** Mapbox terrain tilesets used for contours, hillshade and 3D. */
export const MAPBOX_TERRAIN = {
    contours: "mapbox://mapbox.mapbox-terrain-v2",
    dem: "mapbox://mapbox.mapbox-terrain-dem-v1",
};
